/**
 * PestButtons — three config-driven field-recording buttons.
 *
 *   IncidenceButton (blue)  — tap to toggle; % badge top-right.
 *   LevelButton     (purple)— tap = raise, long-press = lower; ordinal glyph.
 *   CountButton     (green) — tap = +1,    long-press = −1;    numeric value.
 *
 * Spec refs: Implementation Spec §3.4 (layout), §3.5 (long-press), §3.6 (states).
 * Slice 2 / Task S2-10.
 *
 * Long-press contract (§3.5):
 *   - 420 ms timer started on pointerDown.
 *   - If timer fires: longFiredRef = true, call onLongPress.
 *   - pointerUp / pointerLeave: clear timer.
 *   - onClick: if longFiredRef → reset + suppress; else → call onTap.
 *   Survives mouse, touch, and synthetic click.
 *
 * CSS rule (non-negotiable):
 *   NO transition on background / border-color of state-toggling buttons.
 *   transform: scale(.95) press transition is fine (GPU composited).
 *
 * Over-threshold rule (non-negotiable):
 *   overThreshold = red ! flag (top-left) + red badge ONLY.
 *   NEVER changes the outline/border — that is reserved for `infestedHere`.
 *   The two signals are orthogonal.
 */
import React, { useCallback, useRef } from 'react';
import './PestButtons.css';

// ────────────────────────────────────────────────────────────────────────────
// Ordinal glyphs for LevelButton
// ────────────────────────────────────────────────────────────────────────────

const ORD: readonly string[] = ['–', 'x', 'xx', 'xxx', 'xxxx'];

// ────────────────────────────────────────────────────────────────────────────
// Shared long-press hook (file-internal — not exported)
//
// Returns pointer-event handlers to spread onto a <button>.
// Caller is responsible for NOT having any transition on background/border-color.
// ────────────────────────────────────────────────────────────────────────────

interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

function useLongPress(
  onTap: (() => void) | undefined,
  onLongPress: (() => void) | undefined,
  ms = 420,
): LongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longFiredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePointerDown = useCallback(() => {
    longFiredRef.current = false;
    if (onLongPress) {
      timerRef.current = setTimeout(() => {
        longFiredRef.current = true;
        onLongPress();
      }, ms);
    }
  }, [onLongPress, ms]);

  const handlePointerUp = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handlePointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handleClick = useCallback(() => {
    if (longFiredRef.current) {
      longFiredRef.current = false;
      return; // long-press already handled — swallow the click
    }
    onTap?.();
  }, [onTap]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Prevent the native context menu on long-press (mobile Safari, Chrome).
    e.preventDefault();
  }, []);

  return {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerLeave: handlePointerLeave,
    onClick: handleClick,
    onContextMenu: handleContextMenu,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// OverThreshold flag (shared across all three button types)
// ────────────────────────────────────────────────────────────────────────────

function OverFlag() {
  return <span className="pbtn__flag" aria-label="over threshold">!</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// IncidenceButton (blue)
// ────────────────────────────────────────────────────────────────────────────

export interface IncidenceButtonProps {
  code: string;
  name: string;
  pct: number;
  selected: boolean;
  infestedHere: boolean;
  overThreshold: boolean;
  /** Tap toggles the infested verdict at the current point (and selects for the grid). */
  onTap: () => void;
  /** Long-press selects this pest for the dot-grid display WITHOUT toggling a verdict. */
  onLongPress?: () => void;
}

export const IncidenceButton: React.FC<IncidenceButtonProps> = ({
  code,
  name,
  pct,
  selected,
  infestedHere,
  overThreshold,
  onTap,
  onLongPress,
}) => {
  const handlers = useLongPress(onTap, onLongPress);

  const cls = [
    'pbtn',
    'pbtn--inc',
    selected && 'pbtn--selected',
    infestedHere && 'pbtn--infested',
  ]
    .filter(Boolean)
    .join(' ');

  const badgeCls = ['pbtn__badge', overThreshold && 'pbtn__badge--over']
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={cls} {...handlers}>
      {overThreshold && <OverFlag />}
      <span className={badgeCls}>{pct}%</span>
      <span className="pbtn__code">{code}</span>
      <span className="pbtn__name">{name}</span>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// LevelButton (purple)
// ────────────────────────────────────────────────────────────────────────────

export interface LevelButtonProps {
  code: string;
  name: string;
  severity: 0 | 1 | 2 | 3 | 4;
  active: boolean;
  overThreshold: boolean;
  onTap: () => void;
  onLongPress: () => void;
}

export const LevelButton: React.FC<LevelButtonProps> = ({
  code,
  name,
  severity,
  active,
  overThreshold,
  onTap,
  onLongPress,
}) => {
  const handlers = useLongPress(onTap, onLongPress);

  const cls = [
    'pbtn',
    'pbtn--lev',
    active && 'pbtn--active',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={cls} {...handlers}>
      {overThreshold && <OverFlag />}
      <span className="pbtn__code">{code}</span>
      <span className="pbtn__name">{name}</span>
      <span className="pbtn__ord">{ORD[severity]}</span>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// CountButton (green)
// ────────────────────────────────────────────────────────────────────────────

export interface CountButtonProps {
  code: string;
  name: string;
  value: number;
  active: boolean;
  overThreshold: boolean;
  onTap: () => void;
  onLongPress: () => void;
}

export const CountButton: React.FC<CountButtonProps> = ({
  code,
  name,
  value,
  active,
  overThreshold,
  onTap,
  onLongPress,
}) => {
  const handlers = useLongPress(onTap, onLongPress);

  const cls = [
    'pbtn',
    'pbtn--cnt',
    active && 'pbtn--active',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={cls} {...handlers}>
      {overThreshold && <OverFlag />}
      <span className="pbtn__code">{code}</span>
      <span className="pbtn__name">{name}</span>
      <span className="pbtn__value">{value}</span>
    </button>
  );
};
